/**
 * safeDate — defensive coercion for values that MIGHT be a Firestore Timestamp,
 * a Date, an ISO string, or missing entirely.
 *
 * Several Firestore reads used to rely on `orderBy('someDate')` to silently drop
 * docs missing that field; when those queries were replaced with client-side
 * filtering, a malformed doc (no date, or a string date) can now reach the
 * render. Calling `.toDate()` directly on such a value throws and white-screens
 * the page. Use these helpers at every render site that formats a stored date.
 */

type MaybeTimestamp =
  | { toDate: () => Date }
  | Date
  | string
  | number
  | null
  | undefined;

/** Best-effort conversion to a JS Date, or null if it isn't a usable date. */
export function toJsDate(value: MaybeTimestamp): Date | null {
  if (value == null) return null;
  try {
    // Firestore Timestamp
    if (typeof (value as { toDate?: unknown }).toDate === 'function') {
      const d = (value as { toDate: () => Date }).toDate();
      return isNaN(d.getTime()) ? null : d;
    }
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
  } catch {
    return null;
  }
  return null;
}

/** Format a maybe-date for display, returning `fallback` when it can't be parsed. */
export function formatDate(
  value: MaybeTimestamp,
  options?: Intl.DateTimeFormatOptions,
  fallback = 'TBD'
): string {
  const d = toJsDate(value);
  if (!d) return fallback;
  return options ? d.toLocaleDateString(undefined, options) : d.toLocaleDateString();
}

/** Milliseconds since epoch for sorting; 0 when unparseable (sorts oldest). */
export function toMillis(value: MaybeTimestamp): number {
  const d = toJsDate(value);
  return d ? d.getTime() : 0;
}
